import { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, PackagePlus, ListChecks, Building2, Truck, LogOut, Menu, Briefcase, BookOpen, History,
  Tag, Printer, Download, FileText, RefreshCw, CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogoMark } from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TrialBanner } from "@/components/BillingGate";

const BILLING_ENABLED = String(process.env.REACT_APP_BILLING_ENABLED || "true").toLowerCase() !== "false";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/requests/new", label: "New Request", icon: PackagePlus, testid: "nav-new-request" },
  { to: "/requests", label: "All Requests", icon: ListChecks, testid: "nav-requests", end: true },
  { to: "/suppliers", label: "Suppliers", icon: Building2, testid: "nav-suppliers" },
  { to: "/couriers", label: "Couriers", icon: Truck, testid: "nav-couriers" },
  { to: "/company", label: "My Company", icon: Briefcase, testid: "nav-company" },
  ...(BILLING_ENABLED ? [{ to: "/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" }] : []),
  { to: "/audit", label: "Activity", icon: History, testid: "nav-audit" },
  { to: "/docs", label: "Docs", icon: BookOpen, testid: "nav-docs" },
];

function BrandBlock() {
  return (
    <div className="flex items-center gap-3">
      <LogoMark size={40} className="text-foreground" />
      <div>
        <div className="font-heading text-base font-black tracking-tight leading-none">
          OUT <span className="text-orange-600">&amp;</span> IN
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1.5">Dispatch Ops</div>
      </div>
    </div>
  );
}

function LabelsList({ onNavigate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/requests");
      setRows((data || []).slice(0, 8));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openLabelsWindow = (id, action) => {
    onNavigate?.();
    const win = window.open(`/requests/${id}/labels?action=${action}`, "_blank");
    if (!win) toast.error("Popup blocked — allow popups for this site");
  };

  const actionPrint = (id) => openLabelsWindow(id, "print");
  const actionDownload = (id) => openLabelsWindow(id, "download");
  const actionPdf = (id) => openLabelsWindow(id, "pdf");

  return (
    <div className="px-3 pt-4 pb-3 border-t-2 border-border">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-bold text-muted-foreground">
          <Tag className="h-3 w-3 text-orange-600" /> Labels
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          aria-label="Refresh labels"
          data-testid="labels-refresh"
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {rows.length === 0 && (
        <div className="text-[11px] text-muted-foreground px-1 py-2" data-testid="labels-empty">
          {loading ? "Loading…" : "No requests yet"}
        </div>
      )}
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.request_id} data-testid={`sidebar-label-row-${r.reference_number}`} className="group border border-border bg-card hover:border-orange-600 transition-colors">
            <Link
              to={`/requests/${r.request_id}/labels`}
              onClick={onNavigate}
              className="block px-2.5 py-2"
              data-testid={`sidebar-label-open-${r.reference_number}`}
            >
              <div className="font-mono text-[11px] font-bold truncate">{r.reference_number}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {r.delivery?.company || r.pickup?.company || "—"}
              </div>
            </Link>
            <div className="flex border-t border-border">
              <button
                type="button"
                onClick={() => actionPrint(r.request_id)}
                data-testid={`sidebar-label-print-${r.reference_number}`}
                title="Print"
                className="flex-1 h-7 flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <Printer className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => actionDownload(r.request_id)}
                data-testid={`sidebar-label-download-${r.reference_number}`}
                title="Download HTML"
                className="flex-1 h-7 flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground border-l border-border"
              >
                <Download className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => actionPdf(r.request_id)}
                data-testid={`sidebar-label-pdf-${r.reference_number}`}
                title="Download PDF"
                className="flex-1 h-7 flex items-center justify-center text-white bg-orange-600 hover:bg-orange-700 border-l border-border"
              >
                <FileText className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto">
      <div className="p-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 text-sm font-medium border-l-2 transition-colors duration-200 ${
                isActive
                  ? "border-orange-600 bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-accent"
              }`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </div>
      <LabelsList onNavigate={onNavigate} />
    </nav>
  );
}

function UserFooter({ user, onLogout }) {
  const logoUrl = user?.company_logo_url
    ? `${process.env.REACT_APP_BACKEND_URL}${user.company_logo_url}`
    : null;
  return (
    <div className="p-4 border-t-2 border-border space-y-3">
      <div className="flex items-center gap-2">
        {logoUrl && (
          <div className="w-9 h-9 border border-border bg-muted flex items-center justify-center overflow-hidden shrink-0">
            <img src={logoUrl} alt="Company logo" className="max-w-full max-h-full object-contain" />
          </div>
        )}
        <div className="text-xs min-w-0">
          <div className="font-semibold truncate" data-testid="user-name">{user?.company_name || user?.name || user?.email}</div>
          <div className="text-muted-foreground truncate">{user?.email}</div>
        </div>
      </div>
      <Button
        data-testid="logout-button"
        variant="outline"
        onClick={onLogout}
        className="w-full h-9 rounded-none border-2 text-xs uppercase tracking-[0.15em] font-bold"
      >
        <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
      </Button>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const onLogout = async () => { await logout(); nav("/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-64 border-r-2 border-border bg-card hidden md:flex flex-col">
        <div className="px-6 py-6 border-b-2 border-border"><BrandBlock /></div>
        <NavList />
        <UserFooter user={user} onLogout={onLogout} />
      </aside>

      <main className="flex-1 min-w-0">
        <header className="md:hidden sticky top-0 z-20 border-b-2 border-border bg-background/90 backdrop-blur px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={26} className="text-foreground" />
            <div className="font-heading font-black tracking-tight text-sm">
              OUT <span className="text-orange-600">&amp;</span> IN
            </div>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="mobile-menu-button"
                size="icon"
                variant="outline"
                className="rounded-none border-2 h-9 w-9"
                aria-label="Open menu"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              data-testid="mobile-menu-drawer"
              className="w-72 p-0 border-l-2 border-border rounded-none flex flex-col"
            >
              <div className="px-6 py-6 border-b-2 border-border"><BrandBlock /></div>
              <NavList onNavigate={() => setMobileOpen(false)} />
              <UserFooter user={user} onLogout={async () => { setMobileOpen(false); await onLogout(); }} />
            </SheetContent>
          </Sheet>
        </header>
        <div className="p-4 sm:p-6 lg:p-10 space-y-4">
          <TrialBanner />
          {children}
        </div>
      </main>
    </div>
  );
}
